import { ParticleEffectConfig } from '../types/EffectTypes';

/**
 * Predefined particle effect configurations
 */
export const ParticleEffects: Record<string, ParticleEffectConfig> = {
  // ==== COMBAT EFFECTS ====
  hit_spark: {
    id: 'hit_spark',
    type: 'burst',
    texture: 'hit_spark',
    poolSize: 30,
    parameters: {
      speed: { min: 50, max: 150 },
      scale: { start: 1, end: 0 },
      lifespan: 400,
      alpha: { start: 1, end: 0 },
      quantity: 5,
      angle: { min: 0, max: 360 },
      blendMode: 'ADD'
    }
  },

  hit_spark_critical: {
    id: 'hit_spark_critical',
    type: 'burst',
    texture: 'hit_spark',
    poolSize: 50,
    parameters: {
      speed: { min: 100, max: 250 },
      scale: { start: 1.5, end: 0 },
      lifespan: 600,
      alpha: { start: 1, end: 0 },
      quantity: 10,
      angle: { min: 0, max: 360 },
      blendMode: 'ADD',
      tint: 0xffaa00
    }
  },

  healing_particles: {
    id: 'healing_particles',
    type: 'burst',
    texture: 'sparkle',
    poolSize: 20,
    parameters: {
      speed: { min: 30, max: 80 },
      scale: { start: 0.8, end: 0 },
      lifespan: 800,
      alpha: { start: 0.9, end: 0 },
      quantity: 6,
      angle: { min: 270, max: 270 },
      gravityY: -50,
      tint: 0x00ff00,
      blendMode: 'ADD'
    }
  },

  // ==== ABILITY EFFECTS ====
  fire_burst: {
    id: 'fire_burst',
    type: 'burst',
    texture: 'fire_particle',
    poolSize: 40,
    parameters: {
      speed: { min: 20, max: 100 },
      scale: { start: 1, end: 0 },
      lifespan: 800,
      alpha: { start: 1, end: 0 },
      quantity: 8,
      angle: { min: 0, max: 360 },
      gravityY: -20,
      tint: [0xff0000, 0xff6600, 0xffff00],
      blendMode: 'ADD',
      glowTexture: 'glow_soft'
    }
  },

  ice_burst: {
    id: 'ice_burst',
    type: 'burst',
    texture: 'ice_particle',
    poolSize: 30,
    parameters: {
      speed: { min: 10, max: 60 },
      scale: { start: 0.8, end: 0 },
      lifespan: 1200,
      alpha: { start: 0.9, end: 0 },
      quantity: 6,
      angle: { min: 0, max: 360 },
      gravityY: -10,
      tint: [0xddf3ff, 0x88ccff],
      colorEnd: 0x4488cc,
      blendMode: 'ADD',
      glowTexture: 'glow_soft'
    }
  },

  lightning_burst: {
    id: 'lightning_burst',
    type: 'burst',
    texture: 'lightning_particle',
    poolSize: 20,
    parameters: {
      speed: { min: 80, max: 200 },
      scale: { start: 1, end: 0 },
      lifespan: 500,
      alpha: { start: 1, end: 0 },
      quantity: 4,
      angle: { min: 0, max: 360 },
      blendMode: 'ADD',
      tint: [0xffffaa, 0xffff00],
      glowTexture: 'glow_soft'
    }
  },

  nature_burst: {
    id: 'nature_burst',
    type: 'burst',
    texture: 'nature_particle',
    poolSize: 30,
    parameters: {
      // Leaf-flutter read: slower than fire, slight downward drift, long life.
      speed: { min: 15, max: 70 },
      scale: { start: 0.9, end: 0 },
      lifespan: 1000,
      alpha: { start: 0.95, end: 0 },
      quantity: 7,
      angle: { min: 0, max: 360 },
      gravityY: 25,
      tint: [0x44cc44, 0x88ff88, 0x22aa22],
      blendMode: 'ADD',
      glowTexture: 'glow_soft'
    }
  },

  shadow_burst: {
    id: 'shadow_burst',
    type: 'burst',
    texture: 'shadow_particle',
    poolSize: 30,
    parameters: {
      // Smoky implosion read: quick, tight, rising wisps. Additive purple
      // over dark scenes needs HIGH saturation to register.
      speed: { min: 25, max: 90 },
      scale: { start: 1.1, end: 0 },
      lifespan: 700,
      alpha: { start: 1, end: 0 },
      quantity: 7,
      angle: { min: 0, max: 360 },
      gravityY: -15,
      tint: [0xbb66ff, 0x9d4edd, 0x7b2cbf],
      blendMode: 'ADD',
      glowTexture: 'glow_soft'
    }
  },

  holy_burst: {
    id: 'holy_burst',
    type: 'burst',
    texture: 'holy_particle',
    poolSize: 30,
    parameters: {
      // Rising golden motes.
      speed: { min: 20, max: 75 },
      scale: { start: 0.9, end: 0 },
      lifespan: 900,
      alpha: { start: 1, end: 0 },
      quantity: 6,
      angle: { min: 240, max: 300 },
      gravityY: -40,
      tint: [0xffd700, 0xfff3b0, 0xffffff],
      blendMode: 'ADD',
      glowTexture: 'glow_soft'
    }
  },

  arcane_burst: {
    id: 'arcane_burst',
    type: 'burst',
    texture: 'sparkle',
    poolSize: 40,
    parameters: {
      speed: { min: 40, max: 120 },
      scale: { start: 0.7, end: 0 },
      lifespan: 1000,
      alpha: { start: 0.8, end: 0 },
      quantity: 12,
      angle: { min: 0, max: 360 },
      gravityY: -30,
      tint: [0xd7bdf2, 0xaa00ff],
      blendMode: 'ADD',
      glowTexture: 'glow_soft'
    }
  },

  // ==== ITEM & ENVIRONMENT EFFECTS ====
  sparkle: {
    id: 'sparkle',
    type: 'emitter',
    texture: 'sparkle',
    poolSize: 20,
    parameters: {
      speed: { min: 10, max: 30 },
      scale: { start: 0.5, end: 0 },
      lifespan: 1000,
      alpha: { start: 1, end: 0 },
      quantity: 1,
      frequency: 100,
      gravityY: -50,
      angle: { min: 0, max: 360 },
      blendMode: 'ADD'
    }
  },

  sparkle_burst: {
    id: 'sparkle_burst',
    type: 'burst',
    texture: 'sparkle',
    poolSize: 30,
    parameters: {
      speed: { min: 20, max: 80 },
      scale: { start: 0.6, end: 0 },
      lifespan: 1200,
      alpha: { start: 0.9, end: 0 },
      quantity: 15,
      angle: { min: 0, max: 360 },
      gravityY: -40,
      blendMode: 'ADD'
    }
  },

  landing_dust: {
    id: 'landing_dust',
    type: 'burst',
    texture: 'particle_default',
    poolSize: 20,
    parameters: {
      speed: { min: 30, max: 80 },
      scale: { start: 1, end: 0.5 },
      lifespan: 400,
      alpha: { start: 0.8, end: 0 },
      quantity: 3,
      angle: { min: 180, max: 360 },
      gravityY: -20
    }
  },

  teleport_particles: {
    id: 'teleport_particles',
    type: 'emitter',
    texture: 'particle_blue',
    poolSize: 30,
    parameters: {
      speed: { min: 20, max: 60 },
      scale: { start: 0.7, end: 0 },
      lifespan: 1500,
      alpha: { start: 0.8, end: 0 },
      quantity: 1,
      frequency: 50,
      gravityY: -80,
      angle: { min: 0, max: 360 },
      blendMode: 'ADD',
      tint: 0x0088ff
    }
  },

  water_splash: {
    id: 'water_splash',
    type: 'burst',
    texture: 'particle_default',
    poolSize: 20,
    parameters: {
      speed: { min: 40, max: 100 },
      scale: { start: 0.8, end: 0 },
      lifespan: 600,
      alpha: { start: 0.7, end: 0 },
      quantity: 7,
      angle: { min: 180, max: 360 },
      gravityY: -30,
      tint: 0x0088ff
    }
  },

  smoke: {
    id: 'smoke',
    type: 'emitter',
    texture: 'particle_default',
    poolSize: 25,
    parameters: {
      speed: { min: 5, max: 20 },
      scale: { start: 0.5, end: 1.5 },
      lifespan: 2000,
      alpha: { start: 0.3, end: 0 },
      quantity: 1,
      frequency: 200,
      gravityY: -10,
      angle: { min: 0, max: 360 },
      tint: 0x888888
    }
  },

  // ==== UI EFFECTS ====
  ui_sparkle: {
    id: 'ui_sparkle',
    type: 'burst',
    texture: 'sparkle',
    poolSize: 15,
    parameters: {
      speed: { min: 10, max: 40 },
      scale: { start: 0.4, end: 0 },
      lifespan: 800,
      alpha: { start: 0.9, end: 0 },
      quantity: 3,
      angle: { min: 0, max: 360 },
      gravityY: -20,
      blendMode: 'ADD'
    }
  }
};

/**
 * Get particle effect configuration by ID
 */
export function getParticleEffect(id: keyof typeof ParticleEffects): ParticleEffectConfig {
  return ParticleEffects[id] || ParticleEffects.sparkle;
}

/**
 * Get all particle effect IDs
 */
export function getParticleEffectIds(): string[] {
  return Object.keys(ParticleEffects);
}
/**
 * School → particle burst id (VFX expansion, 2026-07-16). The SSOT for which
 * particle family a school's generated/derived effects carry — used by
 * buildSchoolEffect, buildArchetypeEffectConfig, and the school hit defs.
 * Every value must be a real ParticleEffects id (unit-guarded).
 */
export const SCHOOL_PARTICLE_BURST: Record<string, string> = {
  physical: 'hit_spark',
  fire: 'fire_burst',
  frost: 'ice_burst',
  ice: 'ice_burst',
  lightning: 'lightning_burst',
  arcane: 'arcane_burst',
  shadow: 'shadow_burst',
  nature: 'nature_burst',
  holy: 'holy_burst',
  healing: 'healing_particles',
  chaos: 'fire_burst',
  various: 'sparkle_burst',
};
